/**
 * Print row (assets/print-row.css): an endless row of vintage prints, the
 * shared engine behind every carousel of them. Each photo or video is held
 * in the period frame that fits its shape and type, from the shared frames
 * in assets/vintage-frames.js: a Polaroid, a deckle-edged or 6x7 print, a
 * glossy, a slide, a length of Super 8 or camcorder playback.
 *
 * A section renders the empty row (.print-row > .print-row__scroller >
 * .print-row__list) already at its final height, so the prints arriving
 * shift nothing on the page, and an adapter tells the engine where its
 * prints come from:
 *
 *   PrintRow.register("[data-my-carousel]", async (container) => items);
 *
 * Each matching container is loaded once it is within a screen of view
 * (and again when the theme editor re-renders its section). The loader
 * returns the prints as items:
 *
 *   { key, type, width, height, src, video, poster, original, label, context }
 *
 * key: stable per photo, seeding its frame and place; type: "image",
 * "video" or "page"; width and height: the medium's own; src: the image a print
 * shows (a thumbnail); video and poster: a video's file and still; original:
 * the full-size photo shown up close, if any; label: what the print is,
 * for its button and alt text; context(): an optional element shown under
 * the print up close (a product's tags, an Instagram post's caption and
 * link). A page is an element rather than a photo: its item has page(),
 * which builds it (any img[data-src] in it loads as a photo would), and
 * is framed as a magazine page. An empty list, or a loader that throws,
 * hides the container. The adapters are print-carousel-media.js,
 * print-carousel-instagram.js and print-carousel-stories.js, one per source
 * of sections/print-carousel.liquid.
 *
 * The row is endless in both directions: tile k shows item k mod N, so the
 * set repeats, and only a window of tiles is ever on the page. A batch is
 * added on whichever side the shopper (or the row moving on) approaches,
 * and whatever lies well beyond the far side is dropped. A container with
 * data-loop="false" shows its set once instead, from its first print to
 * its last, all of them on the page (for a short set), in the middle
 * while they are narrower than the row. Each tile's media starts loading
 * a row's width before it scrolls into view.
 *
 * Each print gets its own seeded frame, lean, height, curl, film cast and
 * float, so a photo looks the same on every visit. Every few seconds the
 * row moves on, scrolling the next print to the middle (rowAdvance, with
 * data-advance-every on the container), only while it is on screen and the
 * tab is visible, and waiting while the shopper hovers, touches or focuses
 * it. Under a mouse, every print near the cursor reacts to it (rowMagnet);
 * on a touch screen the prints only float, at full size, scrolling as the
 * row does and overlapping a little. After a minute with no
 * input on the page everything rests until the next input. Tapping a print
 * brings it up close (rowZoomIn). Fails quietly: nothing here throws into
 * the page.
 *
 * The engine builds and moves its own tiles, so it cannot hold elements
 * that another script owns and re-renders in place: the product page's
 * Judge.me review cards, whose widget breaks if they are moved out of its
 * list. A page lays such elements out as a row itself, and takes the place
 * and lean a tile would have from the engine:
 *
 *   PrintRow.tileStyle(key, index) -> { "--rot", "--drop", "--shift", "--z" }
 *
 * the same seeded values (see rowTileStyle) for a key and the element's
 * index in its row, to set on the element. Under a mouse, the cursor field
 * can move such elements too, as it moves a row's own prints:
 *
 *   PrintRow.magnet(scroller, list, { slots, pose, tune }) -> field | null
 *
 * where `slots` selects the elements within `list`, `pose(element)` says
 * how the page's stylesheet rests each ({ shift, drop, rot }: pixels,
 * pixels, degrees), so the field starts from that pose and hands the
 * element back to it, and `tune` overrides what the field does (the names
 * of ROW_FIELD) for elements larger than a print. The field reads the
 * elements again when `list` gets a "print-row:tiles" event. It is null
 * without a mouse or under reduced motion, as a row's own is. Nothing else
 * of the engine is involved: no frame, advance or zoom
 * (assets/reviews-row.js). A list that scrolls itself, its elements its own
 * children with no content around them to scroll, is the scroller too:
 * `scroller` and `list` are the one element, and `scrolls: true` among the
 * options says the elements are measured in what it has scrolled
 * (assets/product-rows.js). The list must be positioned, to be what its
 * elements are measured from.
 */

// Several sections may each load this file; the first copy runs, and any
// later one must be a no-op rather than redeclaring these bindings.
(() => {
  if (window.PrintRow) return;

  /* Inline video is skipped on metered or slow connections and for readers who
   ask for reduced motion; those tiles show the poster frame instead. */
  const rowConnection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection;
  const rowSavesData =
    rowConnection &&
    (rowConnection.saveData ||
      /(^|\W)(2g|slow-2g|3g)$/.test(rowConnection.effectiveType || ""));
  const rowReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  const rowAllowInlineVideo = !rowSavesData && !rowReducedMotion;

  /* One observer for every video: a video is fetched and plays only once
   half of it is on screen, and pauses as it leaves. Until then its tile
   shows its still. The media CDN answers a video request with the whole
   file, so a row that preloaded its videos, or played those just beside
   the screen, fetched 6.6MB in ten seconds on a phone's cart page, where
   the footer's Instagram row is in view from the start. */
  const rowVideoObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (!entry.isIntersecting) {
          video.pause();
          return;
        }
        if (!video.getAttribute("src")) video.src = video.dataset.src;
        video.play().catch(() => {});
      });
    },
    { threshold: 0.5 }
  );

  const rowMedia = (item) => {
    if (item.type === "page") return item.page();
    if (item.type === "video" && item.video && rowAllowInlineVideo) {
      const video = document.createElement("video");
      video.width = 720;
      video.height = 900;
      // The still is fetched ahead, like a photo (rowLoadMedia); the video
      // itself only once it is on screen (rowVideoObserver).
      if (item.poster) video.dataset.poster = item.poster;
      video.dataset.src = item.video;
      video.draggable = false;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = "none";
      if (item.label) video.setAttribute("aria-label", item.label);
      rowVideoObserver.observe(video);
      return video;
    }
    const img = document.createElement("img");
    img.decoding = "async";
    // A long press would pick the photo up to drag it, and dragging near
    // the row's ends makes the browser scroll it at speed.
    img.draggable = false;
    img.width = 720;
    img.height = 900;
    img.alt = item.label || "";
    // A video shown still (the connection or the reader's settings) shows
    // its poster; with neither, the frame stays empty.
    const still = item.src || item.poster;
    if (still) img.dataset.src = still;
    return img;
  };

  /* Media is requested ahead of need rather than with loading="lazy": in a
   horizontal scroller the browser starts a lazy image only once it is all
   but in view, so tiles pop in as the row moves. Each carousel watches its
   own row instead and starts anything within three rows' widths either
   side of what is showing, so even a fast fling finds its photos loaded.
   A video's still is fetched here; the video itself waits for
   rowVideoObserver. */
  const rowLoadMedia = (media) => {
    if (media.tagName !== "IMG" && media.tagName !== "VIDEO") {
      media.querySelectorAll("img[data-src]").forEach(rowLoadMedia);
      return;
    }
    if (media.tagName === "VIDEO") {
      if (media.dataset.poster && !media.poster)
        media.poster = media.dataset.poster;
      return;
    }
    if (media.getAttribute("src") || !media.dataset.src) return;
    media.src = media.dataset.src;
  };

  const rowPreloader = (scroller) =>
    new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          rowLoadMedia(entry.target);
        });
      },
      { root: scroller, rootMargin: "50% 300%" }
    );

  /* A small seeded PRNG (FNV-1a hash into mulberry32), so a print's wear is
   tied to its photo rather than reshuffled on every page view. */
  const rowRandom = (key) => {
    let seed = Array.from(key).reduce(
      (hash, char) => Math.imul(hash ^ char.charCodeAt(0), 16777619),
      2166136261
    );
    return () => {
      seed = (seed + 0x6d2b79f5) | 0;
      let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  /* A float runs on the compositor, but every main-thread frame (the
   row moving on, a scroll, an observer firing) still restyles each print
   that is
   animating, so a whole row floating at once costs several milliseconds a
   frame. Only the prints showing in the row, plus a margin so they are
   already moving as they come in, float. */
  const rowFloater = (scroller) =>
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) =>
          entry.target.classList.toggle("is-floating", entry.isIntersecting)
        );
      },
      { root: scroller, rootMargin: "0px 15%" }
    );

  /* Prints are staggered high and low, alternating as a hand dealing them
   out would, with the odd one sitting on the same side as its neighbour,
   and each leans its own way. Each floats on one of three paths at its own
   pace and phase, its shadow following on the matching path. The seed is
   the photo plus which pass through the set this is, so a photo that comes
   round again is a different print of it (its frame's own character, its
   curl and cast, is seeded the same way; see vintage-frames.js). */
  const rowTileStyle = (key, index) => {
    const random = rowRandom(key);
    const between = (min, max) => min + random() * (max - min);
    const lean = random() < 0.5 ? 1 : -1;
    const height = (index % 2 ? -1 : 1) * (random() < 0.25 ? -1 : 1);
    const path = 1 + Math.floor(random() * 3);
    return {
      "--rot": `${(lean * between(1.5, 7)).toFixed(2)}deg`,
      "--drop": `${(height * between(18, 63)).toFixed(1)}px`,
      "--shift": `${between(-9, 9).toFixed(1)}px`,
      "--z": String(1 + Math.floor(random() * 3)),
      "--float": `print-row-float-${path}`,
      "--float-shadow": `print-row-float-shadow-${path}`,
      "--float-duration": `${between(6, 9.5).toFixed(2)}s`,
      "--float-delay": `${(-between(0, 9.5)).toFixed(2)}s`
    };
  };

  /* How wide a tile is, as a multiple of a Polaroid's width: as wide as
   lets its frame stand as tall as a Polaroid (1.162 of its width), but no
   wider than 1.45 of one. A slide is a smaller object, so it sits a little
   smaller. */
  const ROW_POLAROID_ASPECT = 1.162;
  const rowTileWidth = (frame) =>
    Math.min(1.45, ROW_POLAROID_ASPECT / frame.aspect) *
    (frame.kind === "slide" ? 0.86 : 1);

  // Each print's frame parts and resting offset and lean, for rowMagnet
  // and the zoom.
  const rowRest = new WeakMap();

  const rowTile = (item, index, cycle) => {
    const photo = `${item.key || item.src || index}`;
    const key = `${photo}#${cycle}`;
    const slot = document.createElement("div");
    slot.className = "print-row__tile";
    slot.tabIndex = 0;
    slot.setAttribute("role", "button");
    slot.setAttribute(
      "aria-label",
      `${{ video: "Enlarge video", page: "Enlarge page" }[item.type] || "Enlarge photo"}${item.label ? `: ${item.label}` : ""}`
    );
    const style = rowTileStyle(key, index);
    const media = rowMedia(item);
    // The frame follows the photo, whichever pass this is.
    const frame = window.VintageFrames.build({
      media,
      width: item.width,
      height: item.height,
      type: item.type,
      kind: window.VintageFrames.kindFor(item, photo),
      seed: key
    });
    style["--tile-k"] = rowTileWidth(frame).toFixed(4);
    Object.entries(style).forEach(([name, value]) =>
      slot.style.setProperty(name, value)
    );
    slot.appendChild(frame.root);
    rowRest.set(slot, {
      print: frame.root,
      shadow: frame.shadow,
      lifted: frame.lifted,
      glare: frame.glare,
      shade: frame.shade,
      media,
      type: item.type,
      original: item.type === "video" ? null : item.original,
      context: item.context,
      shift: parseFloat(style["--shift"]),
      drop: parseFloat(style["--drop"]),
      rot: parseFloat(style["--rot"])
    });
    return slot;
  };

  /* Tile k of the endless row: item k mod N. */
  const rowTileAt = (state, k) => {
    const count = state.items.length;
    const item = state.items[((k % count) + count) % count];
    const tile = rowTile(item, k, Math.floor(k / count));
    state.preloader.observe(tile.querySelector(".vframe__media"));
    state.floater.observe(tile);
    return tile;
  };

  const rowRelease = (state, tile) => {
    const media = tile.querySelector(".vframe__media");
    state.preloader.unobserve(media);
    state.floater.unobserve(tile);
    if (media.tagName === "VIDEO") {
      rowVideoObserver.unobserve(media);
      media.pause();
      media.removeAttribute("src");
      media.load();
    }
    tile.remove();
  };

  /* How far ahead of its first tile the row starts: an empty run, every
   print the row grows by at its start taken from it and every one it
   sheds given back (rowHoldStill). Some sixty prints' worth, and reset
   to that whenever no finger can be on the row (rowRebase): iOS draws a
   scroller's whole width, and one of 400,000px takes Safari's page down. */
  const ROW_LEAD = 20000;

  /* The run is the list's padding, not its margin. Every change to a
   margin moves the list's own box, and Chrome counts that as a layout
   shift over the whole list although nothing on screen moves; a padding
   leaves the box where it is. print-row.css adds it to the list's gutter. */
  const rowSetLead = (state, lead) => {
    state.lead = lead;
    state.list.style.setProperty("--print-row-lead", `${lead}px`);
  };

  /* Brings the empty run back to ROW_LEAD, scrolling by the difference so
   nothing on screen moves. Only safe where no finger can be holding the
   row (see rowHoldStill): before the row moves on by itself, as it leaves
   the screen, and as a last resort once the run is three times as long. */
  const rowRebase = (state) => {
    const { list, scroller } = state;
    if (state.once || Math.abs(state.lead - ROW_LEAD) < ROW_LEAD / 2) return;
    const anchor = list.firstElementChild;
    const before = anchor.getBoundingClientRect().left;
    rowSetLead(state, ROW_LEAD);
    scroller.scrollBy({
      left: anchor.getBoundingClientRect().left - before,
      behavior: "instant"
    });
    // The prints keep their place on screen but not in the list, whose
    // padding they are measured from (offsetLeft): whatever measured
    // them measures again.
    list.dispatchEvent(new CustomEvent("print-row:tiles"));
  };

  /* Runs `change` (which adds or drops tiles at the row's start, ahead of
   `anchor`) and gives or takes that much room from the empty run ahead of
   the first tile, so that nothing after it moves and the scroll position
   is never touched. Shifting the scroll position instead to keep the view
   still is what iOS will not do under a finger: it keeps the finger's
   position, the shift becomes a jump, the jump brings an end into reach,
   and the row runs away. And a finger that catches a fling is taken over
   by the scroll without the page being told when it lifts, so the row
   cannot reliably wait for it. Only once the run is used up does the
   scroll position move. */
  const rowHoldStill = (state, anchor, change) => {
    const { scroller } = state;
    const before = anchor.getBoundingClientRect().left;
    change();
    const moved = anchor.getBoundingClientRect().left - before;
    const grown = state.rtl ? -moved : moved;
    const lead = Math.max(0, state.lead - grown);
    const short = grown - (state.lead - lead);
    rowSetLead(state, lead);
    if (short)
      scroller.scrollBy({
        left: state.rtl ? -short : short,
        behavior: "instant"
      });
    if (lead > ROW_LEAD * 3) rowRebase(state);
  };

  /* Drops the tiles at one end (1 the end, -1 the start) that lie more
   than a screen beyond where that end would grow again (rowEdgeMargin).
   Trimming by distance rather than by count keeps the row's ends apart
   whatever its prints' widths: an end it trims can never be near enough
   for growing the other to bring it straight back, which once set the two
   ends growing and trimming each other ten times a second. */
  const rowTrim = (state, side) => {
    const { list, scroller } = state;
    const view = scroller.getBoundingClientRect();
    const keep = rowEdgeMargin(scroller) + view.width;
    const tiles = Array.from(list.children);
    // How far beyond the view a tile lies, on the side its end is on.
    const beyond = (tile) => {
      const box = tile.getBoundingClientRect();
      const after = side > 0 !== state.rtl;
      return after ? box.left - view.right : view.left - box.right;
    };
    const fromEnd = side > 0 ? [...tiles].reverse() : tiles;
    let count = 0;
    while (count < fromEnd.length - 1 && beyond(fromEnd[count]) > keep)
      count += 1;
    if (!count) return;
    if (side > 0) {
      tiles.slice(-count).forEach((tile) => rowRelease(state, tile));
      state.last -= count;
    } else {
      rowHoldStill(state, tiles[count], () => {
        tiles.slice(0, count).forEach((tile) => rowRelease(state, tile));
        state.first += count;
      });
    }
    list.dispatchEvent(new CustomEvent("print-row:tiles"));
  };

  const rowWatchEdges = (state) => {
    state.edges.disconnect();
    state.edges.observe(state.list.firstElementChild);
    state.edges.observe(state.list.lastElementChild);
  };

  /* Adds a batch of tiles on one side (1 the end, -1 the start), and drops
   whatever the other side no longer needs (rowTrim). A row shown once
   has all of its tiles already. */
  const rowExtend = (state, side) => {
    const { list, step } = state;
    if (state.once) return;
    const from = side > 0 ? state.last : state.first - step;
    const fragment = document.createDocumentFragment();
    Array.from({ length: step }, (_, i) => from + i).forEach((k) =>
      fragment.append(rowTileAt(state, k))
    );
    if (side > 0) {
      list.append(fragment);
      state.last += step;
    } else {
      rowHoldStill(state, list.firstElementChild, () => list.prepend(fragment));
      state.first -= step;
    }
    rowTrim(state, -side);
    rowWatchEdges(state);
    list.dispatchEvent(new CustomEvent("print-row:tiles"));
  };

  /* Grows the row on whichever side comes within its margin of view: two
   screens, or 600px at least and 1600px at most, so a fling across a
   phone's narrow row does not reach the end of the prints. */
  const rowEdgeMargin = (scroller) =>
    Math.min(1600, Math.max(600, scroller.clientWidth * 2));
  const rowEdgeObserver = (state) =>
    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.target === state.list.firstElementChild)
            rowExtend(state, -1);
          else if (entry.target === state.list.lastElementChild)
            rowExtend(state, 1);
        });
      },
      {
        root: state.scroller,
        rootMargin: `0px ${rowEdgeMargin(state.scroller)}px`
      }
    );

  /* Resolves once the next frame has painted. */
  const rowAfterPaint = () =>
    new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve)));

  /* How far one tile advances the row, on average (tiles differ in width
   with their frames): from the first tile's middle to the last's, over
   the tiles between, which accounts for their overlap. The cursor field
   passes the slots it measured, as a page's own list (PrintRow.magnet) may
   hold others. */
  const rowStepWidth = (list, tiles = list.children) => {
    const first = tiles[0];
    const last = tiles[tiles.length - 1];
    if (!first) return 0;
    if (tiles.length < 2) return first.offsetWidth;
    const span =
      last.offsetLeft +
      last.offsetWidth / 2 -
      (first.offsetLeft + first.offsetWidth / 2);
    return Math.abs(span) / (tiles.length - 1) || first.offsetWidth;
  };

  /* One observer marks every carousel that is on (or about to come on)
   screen with .is-in-view, which is what lets its prints float,
   and tells that carousel's advance (rowAdvance), if it has one. */
  const rowViewHandlers = new WeakMap();
  const rowViewObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle("is-in-view", entry.isIntersecting);
        const handler = rowViewHandlers.get(entry.target);
        if (handler) handler(entry.isIntersecting);
      });
    },
    { rootMargin: "100px 0px" }
  );

  /* After a minute with no input anywhere on the page, every carousel
   rests: the floats pause and the rows stop moving on, so a page left
   open stops costing anything, while someone simply watching still sees
   the rows move for a good while. Any scroll, pointer movement, key or
   touch wakes them. */
  const ROW_REST_AFTER = 60000;
  const rowRestListeners = new Set();
  let rowResting = false;
  let rowLastInput = performance.now();
  const rowSetResting = (resting) => {
    if (rowResting === resting) return;
    rowResting = resting;
    document.documentElement.classList.toggle("print-row-resting", resting);
    rowRestListeners.forEach((listener) => listener(resting));
  };
  // Checks once the quiet period could have run out, rather than on every
  // input; input only records when it happened.
  const rowCheckRest = () => {
    const quiet = performance.now() - rowLastInput;
    if (quiet >= ROW_REST_AFTER) return rowSetResting(true);
    setTimeout(rowCheckRest, ROW_REST_AFTER - quiet);
  };
  const rowOnInput = () => {
    rowLastInput = performance.now();
    if (!rowResting) return;
    rowSetResting(false);
    setTimeout(rowCheckRest, ROW_REST_AFTER);
  };
  ["pointermove", "pointerdown", "keydown", "wheel", "touchstart"].forEach(
    (type) =>
      window.addEventListener(type, rowOnInput, {
        passive: true,
        capture: true
      })
  );
  setTimeout(rowCheckRest, ROW_REST_AFTER);

  /* While the page scrolls, the floats hold still and pick up again once
   it settles: every frame of a scroll runs on the main thread, and each
   would restyle every floating print with it, which on a slower laptop
   cost the scroll frames. One timer, checked rather than reset on every
   scroll event. */
  const ROW_SCROLL_SETTLE = 250;
  let rowLastScroll = 0;
  let rowScrollTimer = 0;
  const rowCheckScroll = () => {
    const quiet = performance.now() - rowLastScroll;
    if (quiet < ROW_SCROLL_SETTLE) {
      rowScrollTimer = setTimeout(rowCheckScroll, ROW_SCROLL_SETTLE - quiet);
      return;
    }
    rowScrollTimer = 0;
    document.documentElement.classList.remove("print-row-scrolling");
  };
  // The page's own scroll only (a carousel's scroller fires scroll events
  // of its own as the row moves on), which is input too.
  window.addEventListener(
    "scroll",
    () => {
      rowOnInput();
      rowLastScroll = performance.now();
      if (rowScrollTimer) return;
      document.documentElement.classList.add("print-row-scrolling");
      rowScrollTimer = setTimeout(rowCheckScroll, ROW_SCROLL_SETTLE);
    },
    { passive: true }
  );

  /* How far to scroll the row to bring a print to its middle: the one
   nearest the middle now, or `ahead` prints after it in reading order.
   Null when there is no such print. */
  const rowToMiddle = (scroller, list, ahead = 0) => {
    const view = scroller.getBoundingClientRect();
    const middle = view.left + view.width / 2;
    const centers = Array.from(list.children, (tile) => {
      const box = tile.getBoundingClientRect();
      return box.left + box.width / 2;
    });
    const nearest = centers.reduce(
      (best, center, index) =>
        Math.abs(center - middle) < Math.abs(centers[best] - middle)
          ? index
          : best,
      0
    );
    const target = centers[nearest + ahead];
    return target === undefined ? null : target - middle;
  };

  /* How long the row waits after a swipe comes to rest before moving on
   again, and how long its own move takes at most. */
  const ROW_ADVANCE_RESUME = 2000;
  const ROW_ADVANCE_MOVE = 1000;

  /* Every few seconds (the section's "Move on every" setting) the row moves
   on by one print, scrolling the next one smoothly to the middle, as the
   homepage hero changes slides. It is the browser's own smooth scroll, so
   it runs on the compositor and leaves the row drawn by its scroller,
   which only keeps what shows: no animation makes the whole row one GPU
   layer (as an earlier continuous drift did, which iOS could not hold).

   It waits while the pointer is over the row, while a finger is on it,
   until a moment after any scroll it did not make has come to rest (a
   finger that catches a fling scrolls the row without the page being told
   it is down), while it has keyboard
   focus or a print is up close, while the carousel is off screen or the
   tab hidden, and after a minute with no input on the page. `onMove` runs
   just before each move, and as the carousel leaves the screen: moments
   no finger is on the row. Returns { pause, resume }. */
  const rowAdvance = (container, scroller, list, every, onMove) => {
    if (!(every > 0)) return null;
    const holds = new Set();
    let visible = container.classList.contains("is-in-view");
    let timer = 0;
    let ownUntil = 0;
    // The next print in reading order after whichever is nearest the
    // middle, brought to the middle.
    const next = () => {
      onMove();
      const distance = rowToMiddle(scroller, list, 1);
      if (distance === null) return;
      ownUntil = performance.now() + ROW_ADVANCE_MOVE;
      scroller.scrollBy({
        left: distance,
        behavior: rowReducedMotion ? "instant" : "smooth"
      });
    };
    const schedule = () => {
      clearTimeout(timer);
      if (!container.isConnected) return teardown();
      if (holds.size || !visible || document.hidden) return;
      timer = setTimeout(() => {
        next();
        schedule();
      }, every);
    };
    // Each reason's pending release, so holding again (a second touch
    // within the wait) cancels it, and one release never cancels another.
    const releases = new Map();
    const hold = (reason) => {
      clearTimeout(releases.get(reason));
      holds.add(reason);
      clearTimeout(timer);
    };
    const release = (reason, delay = 0) => {
      clearTimeout(releases.get(reason));
      releases.set(
        reason,
        setTimeout(() => {
          holds.delete(reason);
          schedule();
        }, delay)
      );
    };
    const listeners = [
      [
        container,
        "pointerenter",
        (event) => event.pointerType === "mouse" && hold("hover")
      ],
      [
        container,
        "pointerleave",
        (event) => event.pointerType === "mouse" && release("hover")
      ],
      [container, "touchstart", () => hold("touch")],
      [container, "touchend", () => release("touch", ROW_ADVANCE_RESUME)],
      [container, "touchcancel", () => release("touch", ROW_ADVANCE_RESUME)],
      [
        scroller,
        "scroll",
        () => {
          if (performance.now() < ownUntil) return;
          hold("scroll");
          release("scroll", ROW_ADVANCE_RESUME);
        }
      ],
      // Keyboard focus only: a mouse click on the scroller also focuses it,
      // and should not stop the row for good once the pointer leaves.
      [
        container,
        "focusin",
        (event) => event.target.matches(":focus-visible") && hold("focus")
      ],
      [container, "focusout", () => release("focus")],
      [document, "visibilitychange", schedule]
    ];
    const onRest = (resting) => (resting ? hold("rest") : release("rest"));
    const teardown = () => {
      clearTimeout(timer);
      releases.forEach((pending) => clearTimeout(pending));
      rowRestListeners.delete(onRest);
      rowViewHandlers.delete(container);
      listeners.forEach(([target, type, listener]) =>
        target.removeEventListener(type, listener)
      );
    };
    listeners.forEach(([target, type, listener]) =>
      target.addEventListener(type, listener, { passive: true })
    );
    rowViewHandlers.set(container, (inView) => {
      visible = inView;
      if (!inView) onMove();
      schedule();
    });
    rowRestListeners.add(onRest);
    if (rowResting) holds.add("rest");
    schedule();
    return {
      pause: () => hold("zoom"),
      resume: () => release("zoom")
    };
  };

  /* The cursor field, for a mouse or trackpad. Every print near the
   pointer is a small body on a spring, eased toward a target each frame.
   The print under the pointer is drawn toward it: it straightens, rises
   toward the viewer and tilts in 3D so the corner nearest the pointer comes
   forward. Every other print is pushed away from the pointer, the nearest
   furthest, tipping slightly away as it goes, far enough that nothing
   overlaps the print being looked at, so stacking order never has to
   change. The frame loop runs only while the pointer is on the row or a
   print is still settling, then hands each print back to its CSS resting
   pose. The field listens on the row itself, so the section heading is
   neutral. A touch screen has no field: its prints only float. */
  const ROW_FIELD = {
    stiffness: 170,
    damping: 21,
    reach: 5, // in prints either side of the pointer
    falloff: 0.6, // how quickly the push weakens with distance
    tilt: 14, // degrees, at the very edge of the print
    glare: 16, // how far the reflection slides at full tilt, in % of its size
    lift: 24, // pixels the print under the pointer rises
    grow: 0.1, // and how much larger it gets
    pull: 0.08, // how far it leans toward the pointer
    sway: 15, // pixels pushed prints move off the pointer's line
    lean: 1.5, // degrees pushed prints tip away
    clear: 1 // how much of the clearance from the print it is pushed to
  };
  // x, y, z-rotation, x-tilt, y-tilt, lift
  const ROW_CHANNELS = 6;
  // The reflection's resting offset, matching .vframe__glare.
  const ROW_GLARE_REST = -6;
  const rowHovers = window.matchMedia(
    "(hover: hover) and (pointer: fine)"
  ).matches;

  /* How large a print sits in the row, against its full size
   (--print-row-rest-scale). */
  const rowRestScale = (list) =>
    parseFloat(
      getComputedStyle(list).getPropertyValue("--print-row-rest-scale")
    ) || 1;

  /* Writes a style only when it differs from what was last written there,
   since every write costs a restyle. `shown` holds the last values. */
  const rowWrite = (shown, key, element, property, value) => {
    if (shown[key] === value) return;
    shown[key] = value;
    element.style[property] = value;
  };

  /* The reflection and the matte frame's shading for a print tipped by
   (tx, ty) degrees and lifted by `lift` (0 to 1). */
  const rowLight = (shown, rest, tx, ty, lift) => {
    // Which corner is tipped toward the viewer, each axis -1 to 1.
    const cx = -ty / ROW_FIELD.tilt;
    const cy = tx / ROW_FIELD.tilt;
    const tipped = Math.min(1, Math.hypot(cx, cy));
    // Like a mirror, the gloss shows the light's reflection moving away
    // from the corner that tips forward, at twice the tilt, and it
    // strengthens the more steeply the print is angled.
    rowWrite(
      shown,
      "glare",
      rest.glare,
      "transform",
      `translate(${(ROW_GLARE_REST - cx * ROW_FIELD.glare).toFixed(2)}%, ` +
        `${(ROW_GLARE_REST - cy * ROW_FIELD.glare).toFixed(2)}%) rotate(-28deg)`
    );
    rowWrite(
      shown,
      "gloss",
      rest.glare,
      "opacity",
      (0.6 + 0.3 * tipped + 0.1 * lift).toFixed(3)
    );
    rowWrite(shown, "shadow", rest.shadow, "opacity", (1 - lift).toFixed(3));
    rowWrite(shown, "lifted", rest.lifted, "opacity", lift.toFixed(3));
    return (cx + cy) / 2;
  };

  /* What a row's print has that an element a page owns does not: the
   lifted shadow, the gloss and the matte's shade. The field writes them
   to this detached element, which shows nothing. */
  const rowSink = document.createElement("div");

  /* `own`, for a page's own elements in place of a row's tiles (see
   PrintRow.magnet): { slots, pose, tune, scrolls }. */
  const rowMagnet = (scroller, list, own = null) => {
    if (rowReducedMotion) return null;
    const tuned = own?.tune ? { ...ROW_FIELD, ...own.tune } : ROW_FIELD;
    const bodies = new Map();
    let suspended = false;
    let pointer = null;
    let frame = 0;
    let last = 0;
    let slots = [];
    let stale = true;
    let geometry = null;
    // Slots never move within the row, so their layout is read once (and
    // again whenever the row grows or sheds tiles), never per frame. Each
    // is measured on its own, as frames differ in shape. Positions are
    // physical left-to-right, so right-to-left rows need no special case.
    const measure = () => {
      stale = false;
      slots = Array.from(
        list.querySelectorAll(own ? own.slots : ".print-row__tile")
      );
      // An element of the page's own is its own print, at the pose its
      // stylesheet gives it now.
      if (own)
        slots.forEach((slot) =>
          rowRest.set(slot, {
            print: slot,
            shadow: rowSink,
            lifted: rowSink,
            glare: rowSink,
            shade: rowSink,
            ...own.pose(slot)
          })
        );
      // The row grows and sheds tiles at its ends; re-index the bodies
      // still on the page and let go of the ones that are not.
      bodies.forEach((body, slot) => {
        body.index = slots.indexOf(slot);
        if (body.index < 0) bodies.delete(slot);
      });
      if (!slots.length) return (geometry = null);
      const scale = rowRestScale(list);
      // Each print as it shows: scaled about the middle of its slot.
      geometry = {
        scale,
        spacing: rowStepWidth(list, slots),
        widths: slots.map((slot) => slot.offsetWidth * scale),
        heights: slots.map((slot) => slot.offsetHeight * scale),
        tops: slots.map(
          (slot) => slot.offsetTop + (slot.offsetHeight * (1 - scale)) / 2
        ),
        centers: slots.map((slot) => slot.offsetLeft + slot.offsetWidth / 2)
      };
    };
    // The print whose middle is nearest `x`.
    const nearestTo = (x) =>
      geometry.centers.reduce(
        (best, center, index) =>
          Math.abs(center - x) < Math.abs(geometry.centers[best] - x)
            ? index
            : best,
        0
      );
    /* How hard the pointer pushes a print `u` prints away, as a multiple
     of the clearance distance. Strongest beside the pointer and steepest
     there too, so the nearest prints respond most to every movement,
     then falling off smoothly to nothing by `reach`. Across the span the
     immediate neighbours can occupy (half a print to one and a half) it
     never drops below 1, so they always clear the print being looked at.
     Inside half a print (the pointer above or below a print rather than
     on it) it eases to 0 so the push never flips sides with a jolt. */
    const field = (u) => {
      const distance = Math.abs(u);
      const slope = tuned.falloff;
      const near =
        distance <= 1.5
          ? 1 + slope * (1.5 - distance)
          : Math.exp(-slope * (distance - 1.5));
      const fade = Math.min(1, distance * 2);
      const edge = Math.min(1, Math.max(0, (tuned.reach - distance) / 1.5));
      return near * fade * edge * edge * (3 - 2 * edge);
    };
    const targetFor = (index, cursor, focus) => {
      const rest = [0, 0, 0, 0, 0, 0];
      if (!cursor) return rest;
      const { spacing, centers } = geometry;
      const width = geometry.widths[index];
      const height = geometry.heights[index];
      const dx = centers[index] - cursor.x;
      const dy =
        geometry.tops[index] +
        rowRest.get(slots[index]).drop +
        height / 2 -
        cursor.y;
      if (index === focus) {
        const cx = Math.max(-1, Math.min(1, -dx / (width / 2)));
        const cy = Math.max(-1, Math.min(1, -dy / (height / 2)));
        return [
          -dx * tuned.pull,
          -dy * tuned.pull - tuned.lift,
          0,
          cy * tuned.tilt,
          -cx * tuned.tilt,
          1
        ];
      }
      // Far enough to clear the lifted print, grown and straightened, from
      // a neighbour's rotated corners, plus the few pixels of seeded shift.
      const repel =
        (width * (0.16 + tuned.grow / 2) + (width - spacing) + 18) *
        tuned.clear;
      if (Math.abs(dx / spacing) > tuned.reach) return rest;
      const strength = field(dx / spacing);
      const side = Math.sign(dx) || 1;
      return [
        side * repel * strength,
        Math.max(-1, Math.min(1, dy / height)) *
          tuned.sway *
          Math.min(1, strength),
        side * tuned.lean * strength,
        0,
        0,
        0
      ];
    };
    const render = (slot, body) => {
      const rest = rowRest.get(slot);
      const { print, shade, shift, drop, rot } = rest;
      const [x, y, rz, tx, ty, lift] = body.value;
      const size = geometry.scale * (1 + tuned.grow * lift);
      rowWrite(
        body.shown,
        "print",
        print,
        "transform",
        `translate(${(shift + x).toFixed(2)}px, ${(drop + y).toFixed(2)}px) ` +
          `perspective(900px) rotateX(${tx.toFixed(2)}deg) rotateY(${ty.toFixed(2)}deg) ` +
          `rotate(${(rot * (1 - lift) + rz).toFixed(2)}deg) ` +
          `scale(${size.toFixed(4)})`
      );
      const facing = rowLight(body.shown, rest, tx, ty, lift);
      // The matte frame is shaded by how squarely it faces the light (up
      // and to the left): tipping the lower right toward the viewer turns
      // it up into the light, tipping the upper left turns it away.
      rowWrite(
        body.shown,
        "shade",
        shade,
        "backgroundColor",
        facing >= 0
          ? `rgb(255 255 255 / ${(facing * 0.14).toFixed(3)})`
          : `rgb(40 28 15 / ${(-facing * 0.12).toFixed(3)})`
      );
    };
    const release = (slot) => {
      const { print, shadow, lifted, glare, shade } = rowRest.get(slot);
      print.style.removeProperty("transform");
      shadow.style.removeProperty("opacity");
      lifted.style.removeProperty("opacity");
      glare.style.removeProperty("transform");
      glare.style.removeProperty("opacity");
      shade.style.removeProperty("background-color");
      shade.style.removeProperty("display");
      lifted.style.removeProperty("display");
      bodies.delete(slot);
    };
    const tick = (now) => {
      /* A first frame's time can be earlier than performance.now() at start. */
      const dt = Math.max(0, Math.min((now - last) / 1000, 1 / 30));
      last = now;
      if (stale) measure();
      if (!geometry) return (frame = 0);
      let cursor = null;
      let focus = -1;
      if (pointer) {
        // Read every frame: the row scrolls under a still pointer.
        const box = list.getBoundingClientRect();
        // A list that is its own scroller (own.scrolls) lays its slots out
        // in its scrolled content, so the pointer is found there.
        const [scrollX, scrollY] = own?.scrolls
          ? [list.scrollLeft - list.clientLeft, list.scrollTop - list.clientTop]
          : [0, 0];
        cursor = {
          x: pointer.x - box.left + scrollX,
          y: pointer.y - box.top + scrollY
        };
        const { centers, widths, heights, tops } = geometry;
        const nearest = nearestTo(cursor.x);
        const lo = Math.max(0, Math.floor(nearest - tuned.reach));
        const hi = Math.min(slots.length - 1, Math.ceil(nearest + tuned.reach));
        for (let index = lo; index <= hi; index++) {
          if (bodies.has(slots[index])) continue;
          // Bring in the layers only a moving print uses; release() hides
          // them again once it settles.
          const rest = rowRest.get(slots[index]);
          rest.lifted.style.display = "block";
          rest.shade.style.display = "block";
          bodies.set(slots[index], {
            index,
            value: new Array(ROW_CHANNELS).fill(0),
            velocity: new Array(ROW_CHANNELS).fill(0),
            shown: {}
          });
        }
        const raised = tops[nearest] + rowRest.get(slots[nearest]).drop;
        if (
          Math.abs(cursor.x - centers[nearest]) <= widths[nearest] / 2 &&
          cursor.y >= raised &&
          cursor.y <= raised + heights[nearest]
        )
          focus = nearest;
      }
      let anyMoving = false;
      bodies.forEach((body, slot) => {
        const target = targetFor(body.index, cursor, focus);
        let moving = false;
        target.forEach((goal, channel) => {
          const offset = body.value[channel] - goal;
          body.velocity[channel] +=
            (-tuned.stiffness * offset -
              tuned.damping * body.velocity[channel]) *
            dt;
          body.value[channel] += body.velocity[channel] * dt;
          if (
            Math.abs(offset) > 0.01 ||
            Math.abs(body.velocity[channel]) > 0.01
          )
            moving = true;
        });
        const resting = target.every((goal) => goal === 0);
        if (moving) anyMoving = true;
        if (!moving && resting) release(slot);
        else render(slot, body);
      });
      /* Only while a print is moving: under a still pointer (which holds
       the row's drift) the prints settle and nothing changes until the
       pointer moves or something scrolls the row under it, and a frame
       asked for every frame would restyle every floating print with it. */
      frame = anyMoving ? requestAnimationFrame(tick) : 0;
    };
    const start = () => {
      if (frame) return;
      last = performance.now();
      frame = requestAnimationFrame(tick);
    };
    // The row moves under a still pointer: the prints follow it.
    const follow = () => {
      if (pointer || bodies.size) start();
    };
    list.addEventListener("print-row:tiles", () => {
      stale = true;
      follow();
    });
    // The page's own resize and scroll, until the row is gone (the theme
    // editor renders a section again with a new one).
    const gone = new AbortController();
    const onPage = (event) => {
      if (!list.isConnected) return gone.abort();
      if (event.type === "resize") stale = true;
      follow();
    };
    ["resize", "scroll"].forEach((type) =>
      window.addEventListener(type, onPage, {
        passive: true,
        signal: gone.signal
      })
    );
    scroller.addEventListener("scroll", follow, { passive: true });
    scroller.addEventListener(
      "pointermove",
      (event) => {
        if (event.pointerType !== "mouse" || suspended) return;
        pointer = { x: event.clientX, y: event.clientY };
        start();
      },
      { passive: true }
    );
    scroller.addEventListener(
      "pointerleave",
      () => {
        pointer = null;
        follow();
      },
      { passive: true }
    );
    // The zoom takes a print out of the row: every print drops straight
    // back to its resting pose and the field waits until the print is back.
    return {
      suspend: () => {
        suspended = true;
        pointer = null;
        cancelAnimationFrame(frame);
        frame = 0;
        Array.from(bodies.keys()).forEach(release);
      },
      resume: () => {
        suspended = false;
        stale = true;
      }
    };
  };

  /* Tap or click a print to bring it up close. It is lifted out of the row
   into a layer above the page and travels from exactly where it sits to
   the middle of the screen, straightening and growing (with a touch of
   overshoot, as if arriving) while its lifted shadow deepens, until it
   covers most of a phone screen or about 60% of a desktop one. The film
   (cast, fade, vignette, gloss, grain) fades away so the photo shows as it
   was taken, and on a good connection the full-size original replaces the
   thumbnail once it has loaded. A magazine page comes up larger, to be
   read. ← and → (or the arrows at the screen's sides, or a swipe across
   it) step to the print beside it in the row (rowZoomStep). A second tap
   or click, Escape, a tap on the backdrop, or scrolling sends it back into
   its place, the film returning as it goes. Every carousel's advance and
   cursor field pause meanwhile. */
  const ROW_ZOOM_MS = rowReducedMotion ? 0 : 650;
  const ROW_ZOOM_EASE = "cubic-bezier(0.2, 1.1, 0.3, 1)";
  // How much of the viewport (width, height) the print may fill; a page,
  // whose text is read up close, nearly all of it.
  const ROW_ZOOM_FIT = {
    phone: [0.94, 0.9],
    desktop: [0.6, 0.65],
    pagePhone: [0.96, 0.92],
    pageDesktop: [0.8, 0.9]
  };
  // How far a swipe across a print up close must travel to step.
  const ROW_ZOOM_SWIPE = 50;
  // Between the print up close and its context panel.
  const ROW_CONTEXT_GAP = 20;
  // The backdrop's z-index, the print's one above and the panel's above
  // that: over everything else on the page, chat bubbles and pop-ups
  // included, which reach for the top of the range too.
  const ROW_LAYER = 2147483645;
  // The section and row values the print's styles read, which it leaves
  // behind when it is lifted out of the row.
  const ROW_ZOOM_VARS = [
    "--rot",
    "--vframe-grain",
    "--vframe-paper",
    "--vframe-shadow",
    "--vframe-table",
    "--film-cast",
    "--film-fade",
    "--film-vignette",
    "--film-edge",
    "--film-gloss",
    "--film-curl",
    "--film-grain",
    "--film-saturate",
    "--film-contrast",
    "--film-warmth"
  ];
  const rowFastConnection = () =>
    !rowSavesData &&
    (!rowConnection ||
      !rowConnection.effectiveType ||
      rowConnection.effectiveType === "4g");
  let rowZoomed = null;
  // Live state per row, by its list, present once its tiles are on the
  // page: how a print up close finds its neighbours (rowNeighbour).
  const rowStates = new WeakMap();
  // Every carousel's pause and resume, so a print up close stills them all.
  const rowCarousels = new Set();
  const rowPauseAll = () => rowCarousels.forEach((hooks) => hooks.pause());
  const rowResumeAll = () => rowCarousels.forEach((hooks) => hooks.resume());

  /* Scrolls a row at once to bring a tile to its middle, unless it is all
   on screen already: after stepping along the row up close, the print
   going back lands where it can be seen. */
  const rowBringIntoView = (slot) => {
    const state = rowStates.get(slot.parentElement);
    if (!state) return;
    const view = state.scroller.getBoundingClientRect();
    const box = slot.getBoundingClientRect();
    if (box.left >= view.left && box.right <= view.right) return;
    state.scroller.scrollBy({
      left: box.left + box.width / 2 - (view.left + view.width / 2),
      behavior: "instant"
    });
  };

  /* Sends the print up close back to its place. Handing over to the print
   beside it (rowZoomStep), the backdrop and arrows stay for that one and
   the carousels stay still. */
  const rowZoomOut = (handOver = false) => {
    const zoom = rowZoomed;
    if (!zoom || zoom.closing) return;
    zoom.closing = true;
    zoom.teardown();
    const { slot, print, rest, panel } = zoom;
    const fading = (
      handOver ? [panel] : [zoom.backdrop, zoom.arrows, panel]
    ).filter(Boolean);
    print.classList.remove("is-zoomed");
    const finish = () => {
      // The journeys hold their end state; let go of every one, or the
      // print's would keep overriding the cursor field's transforms.
      [print, rest.lifted, rest.shadow].forEach((element) =>
        element.getAnimations().forEach((animation) => animation.cancel())
      );
      (rest.thumbnails || []).forEach(([img, src]) => (img.src = src));
      rest.thumbnails = null;
      [
        "position",
        "left",
        "top",
        "width",
        "margin",
        "z-index",
        "transform",
        "cursor"
      ]
        .concat(ROW_ZOOM_VARS)
        .forEach((name) => print.style.removeProperty(name));
      print.removeAttribute("tabindex");
      slot.style.removeProperty("height");
      // Its tile may have been dropped meanwhile, the row growing at the
      // other end as the shopper stepped along it.
      if (slot.isConnected) slot.append(print);
      else print.remove();
      fading.forEach((element) => element.remove());
      // Handed over, the print beside it is up close now.
      if (rowZoomed !== zoom) return;
      rowZoomed = null;
      rowResumeAll();
      if (zoom.returnFocus && slot.isConnected)
        slot.focus({ preventScroll: true });
    };
    if (!slot.isConnected) {
      fading.forEach((element) =>
        element.animate([{ opacity: 1 }, { opacity: 0 }], {
          duration: ROW_ZOOM_MS,
          fill: "forwards"
        })
      );
      print.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: ROW_ZOOM_MS
      }).onfinish = () => {
        print.remove();
        finish();
      };
      return;
    }
    // Back to its place in the row, which has not moved while it was up
    // (its advance was paused, and scrolling the page closes the zoom
    // first) unless the shopper stepped along it.
    if (!handOver) rowBringIntoView(slot);
    const home = slot.getBoundingClientRect();
    // Its resting pose.
    const scale =
      parseFloat(
        getComputedStyle(slot).getPropertyValue("--print-row-rest-scale")
      ) || 1;
    const restPose = `translate(${rest.shift}px, ${rest.drop}px) rotate(${rest.rot}deg) scale(${scale})`;
    const now = getComputedStyle(print).transform;
    zoom.animation.cancel();
    print.style.left = `${home.left}px`;
    print.style.top = `${home.top}px`;
    const timing = {
      duration: ROW_ZOOM_MS,
      easing: "cubic-bezier(0.3, 0, 0.2, 1)",
      fill: "forwards"
    };
    rest.lifted.animate([{ opacity: 1 }, { opacity: 0 }], timing);
    rest.shadow.animate([{ opacity: 0 }, { opacity: 1 }], timing);
    fading.forEach((element) =>
      element.animate([{ opacity: 1 }, { opacity: 0 }], timing)
    );
    print.animate(
      [{ transform: now }, { transform: restPose }],
      timing
    ).onfinish = finish;
  };

  /* The arrows at the screen's sides up close, which step along the row. */
  const rowZoomArrows = () => {
    const arrows = document.createElement("div");
    arrows.className = "print-row-arrows";
    arrows.style.zIndex = ROW_LAYER + 2;
    [
      [-1, "prev", "Previous"],
      [1, "next", "Next"]
    ].forEach(([side, name, label]) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = `print-row-arrow print-row-arrow--${name}`;
      button.setAttribute("aria-label", label);
      button.innerHTML =
        '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
      button.addEventListener("click", () => rowZoomStep(side));
      arrows.append(button);
    });
    document.body.append(arrows);
    return arrows;
  };

  // A print that is in its tile, not up close or on its way back.
  const rowAtHome = (slot) => {
    const rest = slot && rowRest.get(slot);
    return Boolean(rest && slot.contains(rest.print));
  };

  /* Brings a tile's print up close. `carry` is what a step along the row
   hands on from the print before (rowZoomStep): its backdrop and arrows,
   and whether focus goes back to the row at the end. */
  const rowZoomIn = (slot, viaKeyboard, carry = null) => {
    if (rowZoomed && !carry) return;
    if (!rowAtHome(slot)) return;
    const rest = rowRest.get(slot);
    const { print } = rest;
    // The print's own box is its slot's; its current pose (resting, or
    // wherever the cursor field has it) is where the journey starts.
    const box = slot.getBoundingClientRect();
    const from = getComputedStyle(print).transform;
    const values = ROW_ZOOM_VARS.map((name) => [
      name,
      getComputedStyle(print).getPropertyValue(name)
    ]);
    if (!carry) rowPauseAll();
    // The tile keeps its height while the print is out: tiles are centred in
    // the row, so an empty one would collapse to its middle and the way back
    // would land there.
    slot.style.height = `${box.height}px`;
    const backdrop = carry ? carry.backdrop : document.createElement("div");
    if (!carry) {
      backdrop.className = "print-row-backdrop";
      document.body.append(backdrop);
    }
    const arrows = carry ? carry.arrows : rowZoomArrows();
    // What the row's adapter has to say about the print (a product's tags,
    // a post's caption and link), under it, the print sized to leave room.
    const context = rest.context ? rest.context() : null;
    const panel = context ? document.createElement("div") : null;
    if (panel) {
      panel.className = "print-row-context";
      panel.append(context);
      document.body.append(panel);
    }
    const below = panel ? panel.offsetHeight + ROW_CONTEXT_GAP : 0;
    backdrop.style.zIndex = ROW_LAYER;
    if (panel) panel.style.zIndex = ROW_LAYER + 2;
    values.forEach(([name, value]) => print.style.setProperty(name, value));
    Object.assign(print.style, {
      position: "fixed",
      left: `${box.left}px`,
      top: `${box.top}px`,
      width: `${box.width}px`,
      margin: "0",
      zIndex: String(ROW_LAYER + 1),
      transform: from,
      cursor: "zoom-out"
    });
    document.body.append(print);
    print.classList.add("is-zoomed");
    print.tabIndex = -1;
    print.focus({ preventScroll: true });
    const phone = innerWidth < 741;
    const [fitWidth, fitHeight] =
      rest.type === "page"
        ? ROW_ZOOM_FIT[phone ? "pagePhone" : "pageDesktop"]
        : ROW_ZOOM_FIT[phone ? "phone" : "desktop"];
    const scale = Math.min(
      (innerWidth * fitWidth) / box.width,
      ((innerHeight - below) * fitHeight) / box.height
    );
    // The print and its panel, as one group, centred on the screen: the
    // panel just under the print, not down at the screen's edge.
    const top = (innerHeight - box.height * scale - below) / 2;
    const dx = innerWidth / 2 - (box.left + box.width / 2);
    const dy = top + (box.height * scale) / 2 - (box.top + box.height / 2);
    if (panel)
      panel.style.top = `${top + box.height * scale + ROW_CONTEXT_GAP}px`;
    const timing = {
      duration: ROW_ZOOM_MS,
      easing: ROW_ZOOM_EASE,
      fill: "forwards"
    };
    const animation = print.animate(
      [
        { transform: from },
        { transform: `translate(${dx}px, ${dy}px) scale(${scale})` }
      ],
      timing
    );
    rest.lifted.style.display = "block";
    rest.lifted.animate([{ opacity: 0 }, { opacity: 1 }], timing);
    rest.shadow.animate([{ opacity: 1 }, { opacity: 0 }], timing);
    (carry ? [panel] : [backdrop, arrows, panel])
      .filter(Boolean)
      .forEach((element) =>
        element.animate([{ opacity: 0 }, { opacity: 1 }], {
          duration: ROW_ZOOM_MS,
          fill: "forwards"
        })
      );
    // The thumbnail is all a row print needs; up close, fetch the original
    // and swap it in once decoded (and back to the thumbnail on the way out,
    // so a full-size bitmap is not kept for a print in the row). A page's
    // photos each name their own (data-original).
    const originals =
      rest.type === "page"
        ? Array.from(
            rest.media.querySelectorAll("img[data-original]"),
            (img) => [img, img.dataset.original]
          )
        : rest.original
          ? [[rest.media, rest.original]]
          : [];
    if (originals.length && rowFastConnection()) {
      rest.thumbnails = originals.map(([img]) => [
        img,
        img.getAttribute("src")
      ]);
      originals.forEach(([img, original]) => {
        const full = new Image();
        full.decoding = "async";
        full.src = original;
        full
          .decode()
          .then(() => {
            if (rowZoomed && rowZoomed.print === print && !rowZoomed.closing)
              img.src = original;
          })
          .catch(() => {});
      });
    }
    let armed = false;
    const onKey = (event) => {
      if (!armed && event.key !== "Escape") return;
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        rowZoomStep(event.key === "ArrowRight" ? 1 : -1);
        return;
      }
      // Enter on the panel's own link, or on an arrow, follows it.
      if (
        event.key !== "Escape" &&
        (panel?.contains(event.target) || arrows.contains(event.target))
      )
        return;
      if (
        event.key === "Escape" ||
        event.key === "Enter" ||
        event.key === " "
      ) {
        event.preventDefault();
        rowZoomed.returnFocus = true;
        rowZoomOut();
      }
    };
    // A tap, from pointer events rather than click: iOS Safari sends no
    // click for a tap on the lifted-out print.
    let press = null;
    const onPress = (event) => {
      press = { x: event.clientX, y: event.clientY, at: performance.now() };
    };
    const onRelease = (event) => {
      if (!press) return;
      const dx = event.clientX - press.x;
      const dy = event.clientY - press.y;
      const held = performance.now() - press.at;
      press = null;
      // A swipe across steps to the print it uncovers.
      if (Math.abs(dx) > ROW_ZOOM_SWIPE && Math.abs(dx) > 2 * Math.abs(dy))
        rowZoomStep(dx < 0 ? 1 : -1);
      else if (Math.hypot(dx, dy) < 10 && held < 600) rowZoomOut();
    };
    const listeners = [
      [print, "pointerdown", onPress],
      [print, "pointerup", onRelease],
      [backdrop, "pointerdown", onPress],
      [backdrop, "pointerup", onRelease],
      [document, "keydown", onKey],
      // Not rowZoomOut itself: its event would be taken for handOver.
      [window, "scroll", () => rowZoomOut()],
      [window, "resize", () => rowZoomOut()]
    ];
    // A tick later: the click or key that opened the zoom is still on its
    // way up the page and would otherwise close it again. Escape, which
    // cannot have opened it, is heard at once (adding onKey again is a
    // no-op).
    document.addEventListener("keydown", onKey);
    const arm = setTimeout(() => {
      armed = true;
      listeners.forEach(([target, type, listener]) =>
        target.addEventListener(type, listener)
      );
    });
    rowZoomed = {
      slot,
      print,
      rest,
      backdrop,
      arrows,
      panel,
      animation,
      returnFocus: carry ? carry.returnFocus : viaKeyboard,
      teardown: () => {
        clearTimeout(arm);
        listeners.forEach(([target, type, listener]) =>
          target.removeEventListener(type, listener)
        );
        rest.lifted.style.removeProperty("display");
      }
    };
  };

  /* The tile beside one on screen (side 1 to its right, -1 to its left),
   the row growing first if it ends there. */
  const rowNeighbour = (slot, side) => {
    const state = rowStates.get(slot.parentElement);
    if (!state) return null;
    const ahead = state.rtl ? -side : side;
    const beside = () =>
      ahead > 0 ? slot.nextElementSibling : slot.previousElementSibling;
    if (!beside()) rowExtend(state, ahead);
    return beside();
  };

  /* Up close, steps to the print beside this one in the row: it goes back
   to its place as its neighbour comes up, the backdrop staying. A
   neighbour still on its way back from a step before is not stepped to. */
  const rowZoomStep = (side) => {
    const zoom = rowZoomed;
    if (!zoom || zoom.closing) return;
    const slot = rowNeighbour(zoom.slot, side);
    if (!rowAtHome(slot)) return;
    rowZoomOut(true);
    rowZoomIn(slot, false, {
      backdrop: zoom.backdrop,
      arrows: zoom.arrows,
      returnFocus: zoom.returnFocus
    });
  };

  // Fills one row with its items: the first prints, the watcher that grows
  // and sheds the row at its ends, the cursor field, and the
  // advance if the container asks for one.
  const rowMount = async (container, items, generation) => {
    const list = container.querySelector(".print-row__list");
    const scroller = list.parentElement;
    const state = {
      list,
      scroller,
      items,
      step: 0,
      first: 0,
      last: 0,
      lead: 0,
      rtl: getComputedStyle(scroller).direction === "rtl",
      once: container.dataset.loop === "false",
      preloader: rowPreloader(scroller),
      floater: rowFloater(scroller),
      advance: null
    };
    state.edges = rowEdgeObserver(state);
    rowStates.set(list, state);
    // Two prints first, to measure how far apart they sit, then however
    // many more fill the row. Only a few prints fit across at once, and
    // every print on the page adds to each frame's work, so the row is
    // sized to the screen: it grows whenever an end comes within its
    // margin of view (rowEdgeMargin), a batch (a screenful) at a time,
    // and sheds what lies well beyond that margin (rowTrim).
    // Shown once, there are only so many.
    const count = state.once ? items.length : Infinity;
    list.append(
      ...[0, 1].filter((k) => k < count).map((k) => rowTileAt(state, k))
    );
    state.last = list.children.length;
    // The empty run ahead of the first print (ROW_LEAD), scrolled past
    // before anything shows.
    rowRebase(state);
    const spacing = rowStepWidth(list);
    const shown = Math.ceil(scroller.clientWidth / spacing);
    state.step = Math.max(3, shown);
    // Shown once, all of them: it never grows.
    const rest = Array.from(
      { length: (state.once ? count : shown + state.step) - 2 },
      (_, i) => i + 2
    );
    // The rest two at a time, each pair after the previous frame has
    // painted: styling a dozen prints at once is a single long frame
    // just as the row scrolls into view.
    for (let at = 0; at < rest.length; at += 2) {
      await rowAfterPaint();
      if (container.rowGeneration !== generation) return;
      const batch = document.createDocumentFragment();
      rest.slice(at, at + 2).forEach((k) => batch.append(rowTileAt(state, k)));
      list.append(batch);
      state.last = rest[Math.min(at + 1, rest.length - 1)] + 1;
    }
    // A print starts in the middle, as the row rests after each move; the
    // row grows ahead of its start first, so there is room to scroll
    // back to one. Shown once, the row starts at its first print.
    if (!state.once) {
      rowExtend(state, -1);
      scroller.scrollBy({
        left: rowToMiddle(scroller, list) || 0,
        behavior: "instant"
      });
    }

    const field = rowHovers ? rowMagnet(scroller, list) : null;
    state.advance = rowAdvance(
      container,
      scroller,
      list,
      parseInt(container.dataset.advanceEvery, 10),
      () => rowRebase(state)
    );
    rowCarousels.add({
      pause: () => {
        if (field) field.suspend();
        if (state.advance) state.advance.pause();
      },
      resume: () => {
        if (field) field.resume();
        if (state.advance) state.advance.resume();
      }
    });
    list.addEventListener("click", (event) => {
      const slot = event.target.closest(".print-row__tile");
      if (slot) rowZoomIn(slot, false);
    });
    list.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const slot = event.target.closest(".print-row__tile");
      if (!slot) return;
      event.preventDefault();
      rowZoomIn(slot, true);
    });
  };

  // Each registered selector and its loader.
  const rowLoaders = new Map();

  // Loads a container's items and fills its row; hides it if there are none
  // or they cannot be had.
  const rowLoad = async (container) => {
    const loader = [...rowLoaders].find(([selector]) =>
      container.matches(selector)
    )?.[1];
    if (!loader) return;
    // A newer load (the theme editor re-rendering) supersedes this one.
    const generation = (container.rowGeneration || 0) + 1;
    container.rowGeneration = generation;
    try {
      const items = await loader(container);
      if (container.rowGeneration !== generation) return;
      if (!items || !items.length) {
        container.hidden = true;
        return;
      }
      await rowFrames();
      await rowMount(container, items, generation);
    } catch (error) {
      container.hidden = true;
      console.warn("[print-row] could not fill a row:", error);
    }
  };

  // The shared frames' script is deferred too, and may not have run yet;
  // if it never does (blocked, or failed), the row is hidden after a while.
  const ROW_FRAMES_WAIT = 8000;
  const rowFrames = () =>
    window.VintageFrames
      ? Promise.resolve()
      : new Promise((resolve, reject) => {
          document.addEventListener("vintage-frames:ready", resolve, {
            once: true
          });
          setTimeout(
            () => reject(new Error("vintage-frames.js never loaded")),
            ROW_FRAMES_WAIT
          );
        });

  const rowObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        rowObserver.unobserve(entry.target);
        rowLoad(entry.target);
      });
    },
    { rootMargin: "100% 0px" }
  );

  // Containers already being watched: registering again (another adapter,
  // or the same one once per section) must not load one a second time.
  const rowWatched = new WeakSet();

  const rowWatch = (root, selector) => {
    const found = root.matches?.(selector)
      ? [root]
      : root.querySelectorAll(selector);
    found.forEach((container) => {
      if (rowWatched.has(container)) return;
      rowWatched.add(container);
      rowObserver.observe(container);
      rowViewObserver.observe(container);
    });
  };

  // The theme editor re-renders a section in place when a setting changes;
  // this file is already loaded, so pick the new markup up here.
  document.addEventListener("shopify:section:load", (event) =>
    rowLoaders.forEach((_, selector) => rowWatch(event.target, selector))
  );

  window.PrintRow = {
    register: (selector, loader) => {
      rowLoaders.set(selector, loader);
      rowWatch(document, selector);
    },
    // A tile's seeded place and lean, without the float (see the header).
    tileStyle: (key, index) => {
      const style = rowTileStyle(`${key}`, index);
      return {
        "--rot": style["--rot"],
        "--drop": style["--drop"],
        "--shift": style["--shift"],
        "--z": style["--z"]
      };
    },
    // The cursor field over elements a page owns (see the header).
    magnet: (scroller, list, own) =>
      rowHovers ? rowMagnet(scroller, list, own) : null
  };
  document.dispatchEvent(new CustomEvent("print-row:ready"));
})();
